import React, { useEffect, useState } from "react";
import {
  fetchDirectoryAlertOptions,
  submitDirectoryAlertAction,
} from "../utils/directoryAlertsApi";

function DirectoryAlertsModal({ alerts, onClose, onHandled }) {
  const [selectedAlert, setSelectedAlert] = useState(alerts[0] || null);
  const [optionsData, setOptionsData] = useState(null);
  const [selectedSymlinks, setSelectedSymlinks] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!selectedAlert) return;

    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const data = await fetchDirectoryAlertOptions(selectedAlert.id);
        setOptionsData(data);
        setSelectedSymlinks([]);
      } catch (err) {
        alert(err.response?.data?.msg || "Failed to load alert options");
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [selectedAlert]);

  const addSymlinkRow = () => {
    setSelectedSymlinks((prev) => [
      ...prev,
      { chosen_source_path: "", symlink_name: "" },
    ]);
  };

  const updateSymlinkRow = (index, field, value) => {
    setSelectedSymlinks((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      )
    );
  };

  const removeSymlinkRow = (index) => {
    setSelectedSymlinks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleIgnore = async () => {
    if (!selectedAlert) return;

    try {
      setSubmitting(true);
      await submitDirectoryAlertAction(selectedAlert.id, {
        action: "ignore",
      });
      onHandled();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to ignore alert");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSymlinks = async () => {
    if (!selectedAlert) return;

    if (!selectedSymlinks.length) {
      alert("Please add at least one symlink");
      return;
    }

    try {
      setSubmitting(true);
      await submitDirectoryAlertAction(selectedAlert.id, {
        action: "create_symlink",
        symlinks: selectedSymlinks,
      });
      onHandled();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to create symlink");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBranch = async () => {
    if (!selectedAlert) return;

    const ok = window.confirm(
      "Delete this deployed branch/symlink? The mounted source directory will not be deleted."
    );

    if (!ok) return;

    try {
      setSubmitting(true);
      await submitDirectoryAlertAction(selectedAlert.id, {
        action: "delete_branch",
      });
      onHandled();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to delete branch");
    } finally {
      setSubmitting(false);
    }
  };

  if (!alerts.length) return null;

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 2000,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const modalStyle = {
    background: "white",
    width: "850px",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: "25px",
    borderRadius: "8px",
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <h2>Dataset Alerts</h2>

        <p>
          Pending alerts: <strong>{alerts.length}</strong>
        </p>

        <div style={{ display: "flex", gap: "20px" }}>
          <div style={{ width: "35%", borderRight: "1px solid #ddd" }}>
            {alerts.map((alert) => (
              <div
                key={alert.id}
                onClick={() => setSelectedAlert(alert)}
                style={{
                  padding: "10px",
                  cursor: "pointer",
                  background:
                    selectedAlert?.id === alert.id ? "#eee" : "transparent",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <strong>{alert.alert_type}</strong>
                <br />
                <small>{alert.dir_name}</small>
              </div>
            ))}
          </div>

          <div style={{ width: "65%" }}>
            {!selectedAlert ? (
              <p>No alert selected</p>
            ) : (
              <>
                <h3>{selectedAlert.alert_type}</h3>
                <p>{selectedAlert.message}</p>
                <p>
                  <strong>Path:</strong> {selectedAlert.source_path}
                </p>

                {loadingOptions ? (
                  <p>Loading options...</p>
                ) : (
                  <>
                    {selectedAlert.alert_type === "new_uncovered_directory" && (
                      <div>
                        <h4>Create one or multiple symlinks</h4>

                        {selectedSymlinks.map((row, index) => (
                          <div
                            key={index}
                            style={{
                              border: "1px solid #ddd",
                              padding: "10px",
                              marginBottom: "10px",
                            }}
                          >
                            <label>
                              Source directory:
                              <select
                                value={row.chosen_source_path}
                                onChange={(e) =>
                                  updateSymlinkRow(
                                    index,
                                    "chosen_source_path",
                                    e.target.value
                                  )
                                }
                                style={{ width: "100%", marginTop: "5px" }}
                              >
                                <option value="">Choose directory</option>
                                {(optionsData?.options || []).map((opt) => (
                                  <option key={opt.path} value={opt.path}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label style={{ display: "block", marginTop: "10px" }}>
                              Symlink name:
                              <input
                                value={row.symlink_name}
                                onChange={(e) =>
                                  updateSymlinkRow(
                                    index,
                                    "symlink_name",
                                    e.target.value
                                  )
                                }
                                style={{ width: "100%", marginTop: "5px" }}
                                placeholder="example_dataset_name"
                              />
                            </label>

                            <button
                              type="button"
                              className={"button"}
                              onClick={() => removeSymlinkRow(index)}
                              style={{ marginTop: "10px" }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}

                        <button type="button" className={"button"} onClick={addSymlinkRow}>
                          Add symlink
                        </button>

                        <br />
                        <br />

                        <button
                          type="button"
                          className={"button"}
                          onClick={handleCreateSymlinks}
                          disabled={submitting}
                        >
                          Create symlink(s)
                        </button>
                      </div>
                    )}

                    {selectedAlert.alert_type === "empty_subbranch" && (
                      <div>
                        <h4>Empty deployed branch</h4>
                        <p>
                          This branch has no downloadable files. You can delete
                          the deployed symlink/branch or ignore the alert.
                        </p>

                        <button
                          type="button"
                          className={"button"}
                          onClick={handleDeleteBranch}
                          disabled={submitting}
                        >
                          Delete deployed branch
                        </button>
                      </div>
                    )}

                    <hr />

                    <button
                      type="button"
                      className={"button"}
                      onClick={handleIgnore}
                      disabled={submitting}
                    >
                      Ignore alert
                    </button>

                    <button
                      type="button"
                      className={"button"}
                      onClick={onClose}
                      disabled={submitting}
                      style={{ marginLeft: "10px" }}
                    >
                      Close
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DirectoryAlertsModal;